import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  Truck,
  Plus,
  Trash2,
  Globe,
  Save,
  Loader2,
  Users,
  Link as LinkIcon,
  LogOut,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminPartners() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [globalUrl, setGlobalUrl] = useState("");
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", webhook_url: "", default_price: 0, notes: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await Promise.all([refresh(), loadSettings()]);
      } catch {
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    const { data } = await api.get("/admin/partners");
    setPartners(data.partners || []);
  };

  const loadSettings = async () => {
    const { data } = await api.get("/admin/settings");
    setGlobalUrl(data.global_webhook_url || "");
  };

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await api.put("/admin/settings", { global_webhook_url: globalUrl || null });
      toast.success("Global webhook saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingGlobal(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", email: "", webhook_url: "", default_price: 0, notes: "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      email: p.email || "",
      webhook_url: p.webhook_url || "",
      default_price: p.default_price || 0,
      notes: p.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      webhook_url: form.webhook_url.trim() || null,
      default_price: Number(form.default_price) || 0,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) await api.patch(`/admin/partners/${editing.id}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success(editing ? "Partner updated" : "Partner created");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    try {
      await api.delete(`/admin/partners/${id}`);
      toast.success("Partner deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-movewave-paper">
        <div className="text-sm uppercase tracking-wider font-bold">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-movewave-paper">
      <header className="bg-white border-b-2 border-movewave-ink">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 bg-movewave-blue flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl tracking-tight">
              VanLines <span className="text-movewave-blue">Scout</span>
            </span>
            <span className="ml-2 text-xs uppercase tracking-wider text-gray-400 font-bold">/ partners</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-movewave-blue">
              <LayoutDashboard className="w-3.5 h-3.5" /> Leads
            </Link>
            <span className="hidden md:block text-sm text-gray-600">{user?.email}</span>
            <Button
              variant="outline"
              onClick={logout}
              className="rounded-none border-2 border-movewave-ink h-9 px-3 text-xs font-bold uppercase tracking-wider"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Global webhook */}
        <section className="bg-white border-2 border-movewave-ink p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-movewave-ink text-white flex items-center justify-center"><Globe className="w-5 h-5" /></div>
            <div>
              <h2 className="font-display font-black text-2xl tracking-tight">Global webhook</h2>
              <p className="text-sm text-gray-600">Every new lead is POSTed here automatically — useful for Zapier, Make.com, or your CRM.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="https://hooks.zapier.com/hooks/catch/…"
              value={globalUrl}
              onChange={(e) => setGlobalUrl(e.target.value)}
              className="flex-1 h-11 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0"
              data-testid="global-webhook-input"
            />
            <Button
              onClick={saveGlobal}
              disabled={savingGlobal}
              className="rounded-none bg-movewave-ink text-white h-11 px-5 text-xs font-bold uppercase tracking-wider btn-brut"
              data-testid="save-global-webhook-btn"
            >
              {savingGlobal ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-2" /> Save</>}
            </Button>
          </div>
        </section>

        {/* Partners list */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-movewave-blue text-white flex items-center justify-center"><Users className="w-5 h-5" /></div>
              <div>
                <h2 className="font-display font-black text-2xl tracking-tight">Partner movers</h2>
                <p className="text-sm text-gray-600">Vetted companies you sell leads to.</p>
              </div>
            </div>
            <Button
              onClick={openNew}
              className="rounded-none bg-movewave-orange hover:bg-movewave-orange-hover text-white h-11 px-5 text-xs font-bold uppercase tracking-wider btn-brut"
              data-testid="new-partner-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> New partner
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 border-2 border-dashed border-gray-300 p-12 text-center text-gray-500 bg-white">
                No partners yet. Add one to start dispatching leads.
              </div>
            )}
            {partners.map((p) => (
              <div key={p.id} className="bg-white border-2 border-movewave-ink p-5 flex flex-col" data-testid={`partner-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-xl">{p.name}</div>
                    {p.email && <div className="text-sm text-gray-600">{p.email}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-gray-500">Default price</div>
                    <div className="font-display font-black text-2xl">${Number(p.default_price || 0).toFixed(0)}</div>
                  </div>
                </div>
                {p.webhook_url && (
                  <div className="text-xs flex items-center gap-1 text-movewave-blue mb-2 break-all">
                    <LinkIcon className="w-3 h-3 flex-shrink-0" /> {p.webhook_url}
                  </div>
                )}
                {p.notes && <div className="text-sm text-gray-600 mb-3">{p.notes}</div>}
                <div className="mt-auto flex gap-2 pt-3 border-t-2 border-gray-100">
                  <Button onClick={() => openEdit(p)} variant="outline" className="rounded-none border-2 border-movewave-ink h-9 px-3 text-xs font-bold uppercase tracking-wider flex-1">
                    Edit
                  </Button>
                  <Button onClick={() => remove(p.id)} variant="outline" className="rounded-none border-2 border-red-600 text-red-600 hover:bg-red-50 h-9 px-3 text-xs font-bold uppercase tracking-wider">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Partner dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none border-2 border-movewave-ink shadow-[10px_10px_0_#002FA7]">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">
              {editing ? "Edit partner" : "New partner"}
            </DialogTitle>
            <DialogDescription className="text-xs uppercase tracking-wider">
              Add a moving company you sell leads to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Movers" className="h-11 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0" data-testid="partner-name-input" />
            </Field>
            <Field label="Email (optional)">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sales@acme.com" className="h-11 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0" data-testid="partner-email-input" />
            </Field>
            <Field label="Webhook URL (optional)">
              <Input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://…" className="h-11 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0" data-testid="partner-webhook-input" />
            </Field>
            <Field label="Default price (USD)">
              <Input type="number" min="0" step="1" value={form.default_price} onChange={(e) => setForm({ ...form, default_price: e.target.value })} className="h-11 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0" data-testid="partner-price-input" />
            </Field>
            <Field label="Notes (optional)">
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Service area, specialties, etc." className="rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-2 border-movewave-ink h-10 px-4 text-xs font-bold uppercase tracking-wider">Cancel</Button>
              <Button onClick={save} className="rounded-none bg-movewave-blue text-white h-10 px-5 text-xs font-bold uppercase tracking-wider btn-brut" data-testid="save-partner-btn">
                {editing ? "Save changes" : "Create partner"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
